import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  borderGrey,
  darkerBG,
  extraSmallTextSize,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  whiteBG,
} from '../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../services/staff/staffService';
import { AccessRequestStatus } from '../../../../stores/appStaffStore';
import { useStaffStatusActions } from '../hooks/useStaffStatusActions';
import StaffAvatar from './StaffAvatar';
import StaffRestaurantAccessSection from './sections/StaffRestaurantAccessSection';
import StaffPermissionsSection from './sections/StaffPermissionsSection';
import StaffRoleSection from './sections/StaffRoleSection';
import StaffEmailSettingsSection from './sections/StaffEmailSettingsSection';
import StaffAccountStatusSection from './sections/StaffAccountStatusSection';

interface Restaurant {
  id: string;
  name: string;
}

interface StaffDetailPanelProps {
  user: FrontendStaffUser;
  restaurants: Restaurant[];
  /** Account admin — gates the e-mail settings section. Defaults closed. */
  isAdmin?: boolean;
  /** Whether the viewer may manage THIS member. Defaults closed so a caller that
   *  forgets the prop gets the read-only panel, not the unlocked one. */
  canManage?: boolean;
  onClose: () => void;
  onDeleteUser: () => void;
  onRemoveRestaurantAccess: (restaurantId: string, restaurantName: string) => void;
  onManageAccess: (restaurantId: string) => void;
  onAddRestaurant: () => void;
  onPermissionToggle: (permission: string, enabled: boolean) => void;
  onPriceAlertToggle: (enabled: boolean) => void;
  onWeeklyReportsToggle: (enabled: boolean) => void;
  onStaffSignupEmailToggle?: (enabled: boolean) => void;
}

const StaffDetailPanel: React.FC<StaffDetailPanelProps> = ({
  user,
  restaurants,
  isAdmin = false,
  canManage = false,
  onClose,
  onDeleteUser,
  onRemoveRestaurantAccess,
  onManageAccess,
  onAddRestaurant,
  onPermissionToggle,
  onPriceAlertToggle,
  onWeeklyReportsToggle,
  onStaffSignupEmailToggle,
}) => {
  const { t } = useTranslation();
  const { approve, reject } = useStaffStatusActions(user.id);

  const getRestaurantName = (id: string) => restaurants.find((r) => r.id === id)?.name || id;
  const displayName = user.name?.trim() || user.email;
  const roleLabel = user.restaurant_role || '—';
  // A null status is an unresolved request, not an approved one — default to the
  // pending summary so an unknown state never unlocks the management sections.
  const status: AccessRequestStatus = user.access_request || 'pending';
  const isPendingOrRejected = status === 'pending' || status === 'rejected';

  const summaryLabelClass = cn(extraSmallTextSize, secondaryText, 'uppercase tracking-wide');
  const summaryValueClass = cn(smallTextSize, primaryText, semiBoldText, 'mt-1');

  return (
    <div
      className={cn(
        whiteBG,
        'border border-darkGreenColor dark:border-darkGreenColor-dark lg:border-none',
        'rounded-lg sm:rounded-xl p-3 lg:p-4 flex flex-col min-h-0 h-full lg:overflow-hidden'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-5 shrink-0">
        <StaffAvatar user={user} size="md" />
        <div className="flex-1 min-w-0">
          <h2 className={cn(semiBoldText, primaryText, 'text-[18px] leading-6 truncate')}>{displayName}</h2>
          <p className={cn(smallTextSize, secondaryText, 'leading-6 mt-1 truncate')}>{roleLabel}</p>
          {/* Without a name the heading already IS the e-mail — don't print it twice. */}
          {user.email && displayName !== user.email && (
            <a
              href={`mailto:${user.email}`}
              title={user.email}
              className={cn(smallTextSize, tertiaryText, 'leading-5 block truncate hover:underline')}
            >
              {user.email}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'w-10 h-10 flex items-center justify-center rounded-lg border',
            borderGrey,
            tertiaryText,
            'hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors'
          )}
          aria-label={t('common.close')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M15 5L5 15" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 5L15 15" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="space-y-4 sm:space-y-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto hide-scrollbar lg:overscroll-contain">
        {isPendingOrRejected && (
          <div className={cn('rounded-xl p-4 border', borderGrey, darkerBG)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={summaryLabelClass}>{t('staff.name')}</span>
                <p className={summaryValueClass}>{user.name || '—'}</p>
              </div>
              <div>
                <span className={summaryLabelClass}>{t('staff.role')}</span>
                <p className={summaryValueClass}>{user.restaurant_role || '—'}</p>
              </div>
              <div>
                <span className={summaryLabelClass}>{t('staff.restaurant')}</span>
                <p className={summaryValueClass}>
                  {user.restaurant_ids.map((id) => getRestaurantName(id)).join(', ') || '—'}
                </p>
              </div>
              <div>
                <span className={summaryLabelClass}>{t('staff.status')}</span>
                <p className={cn(smallTextSize, 'mt-1')}>
                  {status === 'pending' && (
                    <span className="text-yellow-700 dark:text-yellow-300">{t('staff.accessRequest.pending')}</span>
                  )}
                  {status === 'rejected' && (
                    <span className="text-red-700 dark:text-red-300">{t('staff.accessRequest.rejected')}</span>
                  )}
                </p>
              </div>
            </div>

            {canManage && (
              <div className="flex items-center gap-2 mt-4">
                <button
                  type="button"
                  onClick={approve}
                  className={cn(
                    smallTextSize,
                    semiBoldText,
                    'px-3 py-1.5 rounded-xl text-white dark:text-white',
                    'bg-darkGreenColor dark:bg-darkGreenColor-dark hover:opacity-90'
                  )}
                >
                  {t('staff.actions.approve')}
                </button>
                {status === 'pending' && (
                  <button
                    type="button"
                    onClick={reject}
                    className={cn(
                      smallTextSize,
                      semiBoldText,
                      'px-3 py-1.5 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    )}
                  >
                    {t('staff.actions.reject')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {status === 'approved' && (
          <>
            <StaffRestaurantAccessSection
              user={user}
              restaurants={restaurants}
              canManage={canManage}
              onRemoveRestaurantAccess={onRemoveRestaurantAccess}
              onManageAccess={onManageAccess}
              onAddRestaurant={onAddRestaurant}
            />

            {canManage && <StaffPermissionsSection user={user} onPermissionToggle={onPermissionToggle} />}

            {canManage && <StaffRoleSection user={user} />}
          </>
        )}

        {isAdmin && status === 'approved' && (
          <StaffEmailSettingsSection
            user={user}
            onPriceAlertToggle={onPriceAlertToggle}
            onWeeklyReportsToggle={onWeeklyReportsToggle}
            onStaffSignupEmailToggle={onStaffSignupEmailToggle}
          />
        )}

        {canManage && (
          <StaffAccountStatusSection staffId={user.id} status={status} onDeleteUser={onDeleteUser} />
        )}
      </div>
    </div>
  );
};

export default StaffDetailPanel;
